import React, { useState, useMemo, useCallback } from 'react';
import ColumnFilterPopup from './ColumnFilterPopup';
import { weeksAhead, daysByWeeklyForecast } from '../utils/daysByForecast';

export const compareOp = (val, op, threshold) => {
  if (isNaN(threshold)) return true;
  switch (op) {
    case '>':  return val > threshold;
    case '<':  return val < threshold;
    case '>=': return val >= threshold;
    case '<=': return val <= threshold;
    case '=':  return Math.round(val) === threshold;
    default:   return true;
  }
};

// Uncontrolled inline-edit input: saves on blur/Enter, Escape reverts.
// onSave may return false — or a promise of false, for a save that failed —
// to reject the value (input is reverted).
export const InlineEdit = ({ value, onSave, type = 'number', className = '', placeholder = '—', title }) => (
  <input
    key={String(value ?? '')}
    type={type}
    className={className}
    defaultValue={value ?? ''}
    placeholder={placeholder}
    title={title}
    min={type === 'number' ? 0 : undefined}
    onBlur={e => {
      const input = e.target;
      const v = input.value.trim();
      if (v === String(value ?? '')) return;
      Promise.resolve(onSave(v)).then(ok => { if (ok === false) input.value = value ?? ''; });
    }}
    onKeyDown={e => {
      if (e.key === 'Enter') e.target.blur();
      if (e.key === 'Escape') { e.target.value = value ?? ''; e.target.blur(); }
    }}
  />
);

// Format date as "Mon Apr 14"
export const fmtDateFull = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Returns 5 YYYY-MM-DD strings: the 5 days before shipDate (shipDate-5 .. shipDate-1)
export const getProdDays = (shipDate) => {
  const [y, m, d] = String(shipDate).split('-').map(Number);
  const ship = new Date(y, m - 1, d);
  const days = [];
  for (let i = 5; i >= 1; i--) {
    const day = new Date(ship);
    day.setDate(day.getDate() - i);
    days.push(`${day.getFullYear()}-${String(day.getMonth()+1).padStart(2,'0')}-${String(day.getDate()).padStart(2,'0')}`);
  }
  return days;
};

export const TYPE_LABELS = {
  sets: 'Sets',
  wildmicr: 'Wildflowers & Microgreens',
  individualpackets: 'Individual Packets',
  fert_products: 'Fertilizers',
  readyproducts: 'Ready Products',
  kit: 'Kits',
};

const COLLAPSE_STORAGE_KEY  = 'sp_collapsed_types';
const PROD_PLAN_STORAGE_KEY = 'sp_prod_plan_open';
const SHIPMENT_STORAGE_KEY  = 'sp_shipment_open';
const FBA_STORAGE_KEY       = 'sp_fba_open';

// Days Supplied groups: the same three stock levels, covered by the 7d sales
// velocity (act) or by the AI agent's weekly forecast (ai, columns 'ai_<col>')
const DAYS_COLS = [
  ['cur', 'Cur Stock', 'FBA Available + AWD'],
  ['inbound', '+ Inbound', 'FBA Available + Inbound + AWD'],
  ['ship', '+ Shipment', 'FBA + AWD + Units'],
];
const DAYS_GROUPS = [
  { id: 'act', label: 'Actual Sales', storageKey: 'sp_days_act_open',
    cols: DAYS_COLS.map(([col, label, stock]) => [col, label, `(${stock}) / Velocity 7d`]) },
  { id: 'ai', label: 'Forecast AI', storageKey: 'sp_days_ai_open',
    cols: DAYS_COLS.map(([col, label, stock]) => [`ai_${col}`, label,
      `(${stock}) by the AI weekly forecast: whole weeks, partial ones by the daily rate`]) },
];

// Open/closed flag of a collapsible column group, remembered in localStorage
const useStoredFlag = (key, initial) => {
  const [on, setOn] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved === null ? initial : JSON.parse(saved);
    } catch { return initial; }
  });
  const toggle = useCallback(() => {
    setOn(prev => {
      try { localStorage.setItem(key, JSON.stringify(!prev)); } catch {}
      return !prev;
    });
  }, [key]);
  return [on, toggle];
};

const loadCollapsed = () => {
  try {
    const saved = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveCollapsed = (state) => {
  try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(state)); } catch {}
};

export const fmtTimestamp = (ts) => {
  if (!ts) return null;
  const m = String(ts).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5]);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ', ' + m[4] + ':' + m[5];
};

export const fmtDateShort = (dateStr) => {
  if (!dateStr) return '';
  const p = String(dateStr).split('T')[0].split('-').map(Number);
  return new Date(p[0], p[1]-1, p[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ShipmentsTable = ({
  products, dailyPlan, producedData = {}, shipDate,
  boxInputs, setBoxInputs,
  fbaUpdatedAt, awdUpdatedAt,
  velocityData = {},
  actualSalesData = {},
  onOpenRangePopup,
  forecastSalesData = {},
  aiForecast = {},
  minBoxesFilter = {},
  onSaveBoxInputs,
  onClearBoxInputs,
  boxSaveStatus,
  commentsData = {},
  onSaveComment,
  onSaveFbaStock,
  onSaveAwdStock,
}) => {
  const prodDays = useMemo(() => getProdDays(shipDate), [shipDate]);

  const [collapsedTypes, setCollapsedTypes] = useState(loadCollapsed);

  const [prodPlanOpen, toggleProdPlan] = useStoredFlag(PROD_PLAN_STORAGE_KEY, true);
  const [shipmentOpen, toggleShipment] = useStoredFlag(SHIPMENT_STORAGE_KEY, true);
  const [fbaOpen, toggleFba]           = useStoredFlag(FBA_STORAGE_KEY, false);
  const [daysActOpen, toggleDaysAct]   = useStoredFlag(DAYS_GROUPS[0].storageKey, true);
  const [daysAiOpen, toggleDaysAi]     = useStoredFlag(DAYS_GROUPS[1].storageKey, true);
  const daysOpen   = { act: daysActOpen, ai: daysAiOpen };
  const daysToggle = { act: toggleDaysAct, ai: toggleDaysAi };

  // Sorting by Days Supplied columns (within each product group)
  const [daysSort, setDaysSort] = useState({ col: null, dir: 'asc' });

  // Per-column filters: { awd|reserved|<Days Supplied col>: { operator, value } }
  const [colFilters, setColFilters] = useState({});

  const toggleCollapse = useCallback((type) => {
    setCollapsedTypes(prev => {
      const next = { ...prev, [type]: !prev[type] };
      saveCollapsed(next);
      return next;
    });
  }, []);

  // Collapsing a Days Supplied group also drops its filters and sort — hidden
  // columns must not keep narrowing or reordering the rows
  const toggleDaysGroup = (group) => {
    if (daysOpen[group.id]) {
      const cols = group.cols.map(([col]) => col);
      setColFilters(prev => Object.fromEntries(Object.entries(prev).filter(([col]) => !cols.includes(col))));
      setDaysSort(prev => (cols.includes(prev.col) ? { col: null, dir: 'asc' } : prev));
    }
    daysToggle[group.id]();
  };

  const handleDaysSort = useCallback((col) => {
    setDaysSort(prev =>
      prev.col !== col ? { col, dir: 'asc' }
      : prev.dir === 'asc' ? { col, dir: 'desc' }
      : { col: null, dir: 'asc' }
    );
  }, []);

  const daysInCurrentMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  }, []);

  // Keys of the AI forecast: the current month ('2026-09') and the ISO weeks from today on
  const aiMonthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const aiWeeks = useMemo(() => weeksAhead(), []);

  const actualSalesMeta = useMemo(() => {
    let updatedAt = null, dateFrom = null, dateTo = null;
    Object.values(actualSalesData).forEach(row => {
      if (row.updated_at && (!updatedAt || row.updated_at > updatedAt)) {
        updatedAt = row.updated_at;
        dateFrom  = row.date_from;
        dateTo    = row.date_to;
      }
    });
    return { updatedAt, dateFrom, dateTo };
  }, [actualSalesData]);

  const velUpdatedAt = useMemo(() => {
    let ts14 = null, from14 = null, to14 = null;
    let ts7  = null, from7  = null, to7  = null;
    Object.values(velocityData).forEach(row => {
      if (row.vel_14d_updated_at && (!ts14 || row.vel_14d_updated_at > ts14)) {
        ts14 = row.vel_14d_updated_at;
        from14 = row.vel_14d_from;
        to14   = row.vel_14d_to;
      }
      if (row.vel_7d_updated_at && (!ts7 || row.vel_7d_updated_at > ts7)) {
        ts7 = row.vel_7d_updated_at;
        from7 = row.vel_7d_from;
        to7   = row.vel_7d_to;
      }
    });
    return { ts14, from14, to14, ts7, from7, to7 };
  }, [velocityData]);

  const planLookup = useMemo(() => {
    const map = {};
    dailyPlan.forEach(row => {
      if (!map[row.sku]) map[row.sku] = {};
      map[row.sku][row.date] = (map[row.sku][row.date] || 0) + row.qty_to_produce;
    });
    return map;
  }, [dailyPlan]);

  const handleBoxInput = (sku, val) => {
    const num = val === '' ? '' : Math.max(0, parseInt(val) || 0);
    setBoxInputs(prev => ({ ...prev, [sku]: num }));
  };

  // Days Supplied values: cur = FBA Available only, inbound = full FBA, ship = full FBA + planned units.
  // Each stock level twice: <col> by the 7d velocity, ai_<col> by the AI weekly forecast
  // (beyond[ai_<col>] = the forecast ended before the stock did, the value is its horizon)
  const calcDays = useCallback((p) => {
    const vel = velocityData[p.SKU]?.vel_7d != null ? parseFloat(velocityData[p.SKU].vel_7d) : null;
    const weekly = aiForecast.week?.[p.ASIN];
    const awd      = Number(p.stock_awd) || 0;
    const fbaAvail = Number(p.stock_fba_available) || 0;
    const fbaTotal = Number(p.stock_fba) || 0;
    const boxInput = boxInputs[p.SKU];
    const shipUnits = boxInput !== undefined && boxInput !== '' ? Number(boxInput) * (Number(p.bagsPerBox) || 0) : 0;
    const stock = {
      cur:     fbaAvail + awd,
      inbound: fbaTotal + awd,
      ship:    fbaTotal + awd + shipUnits,
    };
    const days = { beyond: {} };
    Object.entries(stock).forEach(([col, units]) => {
      days[col] = vel != null && vel > 0 ? units / vel : null;
      const ai = weekly ? daysByWeeklyForecast(units, weekly, aiWeeks) : null;
      days[`ai_${col}`] = ai ? ai.days : null;
      days.beyond[`ai_${col}`] = !!ai && ai.beyond;
    });
    return days;
  }, [velocityData, boxInputs, aiForecast, aiWeeks]);

  // Group products by type, applying minBoxesFilter { min, max }
  const grouped = useMemo(() => {
    const order = ['sets', 'wildmicr', 'individualpackets', 'fert_products', 'readyproducts', 'kit'];
    const groups = {};
    const minVal = minBoxesFilter?.min !== '' && minBoxesFilter?.min != null ? Number(minBoxesFilter.min) : null;
    const maxVal = minBoxesFilter?.max !== '' && minBoxesFilter?.max != null ? Number(minBoxesFilter.max) : null;
    const hasFilter = minVal !== null || maxVal !== null;
    const activeColFilters = Object.entries(colFilters).filter(([, f]) => f && f.value !== '');

    products.forEach(p => {
      if (activeColFilters.length > 0) {
        const bpb = Number(p.bagsPerBox) || 0;
        // the values the cells show: AWD total, Reserved boxes (null = '—'), days
        const d = {
          ...calcDays(p),
          awd: Number(p.stock_awd) || 0,
          reserved: bpb > 0 ? (Number(p.shipment_added_box) - Number(p.shipment_needed_box)) * bpb : null,
        };
        const pass = activeColFilters.every(([col, f]) =>
          d[col] != null && compareOp(d[col], f.operator, parseFloat(f.value))
        );
        if (!pass) return;
      }
      if (hasFilter) {
        const sku = p.SKU;
        const bagsPerBox = Number(p.bagsPerBox) || 0;
        if (bagsPerBox > 0) {
          const stockWH = Number(p.stock_wh) || 0;
          const shipAddedPcs = Number(p.shipment_added_box) * bagsPerBox;
          const willBeTaken = shipAddedPcs - Number(p.shipment_needed_box) * bagsPerBox;
          const prodSum = prodDays.reduce((sum, d) => sum + (planLookup[sku]?.[d] || 0), 0);
          const producedSum = prodDays.reduce((sum, d) => sum + (producedData[sku]?.[d] || 0), 0);
          const remainingProd = Math.max(0, prodSum - producedSum);
          const boxesCanShip = Math.floor((stockWH + willBeTaken + remainingProd) / bagsPerBox);
          if (minVal !== null && boxesCanShip < minVal) return;
          if (maxVal !== null && boxesCanShip > maxVal) return;
        } else {
          if (minVal !== null && minVal > 0) return;
        }
      }
      if (!groups[p.type]) groups[p.type] = [];
      groups[p.type].push(p);
    });
    if (daysSort.col) {
      const dir = daysSort.dir === 'desc' ? -1 : 1;
      Object.values(groups).forEach(rows => rows.sort((a, b) => {
        const da = calcDays(a)[daysSort.col];
        const db = calcDays(b)[daysSort.col];
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        return (da - db) * dir;
      }));
    }
    return order.filter(t => groups[t] && groups[t].length > 0).map(t => ({ type: t, rows: groups[t] }));
  }, [products, minBoxesFilter, prodDays, planLookup, producedData, daysSort, colFilters, calcDays]);

  const STICKY_COLS = 5;
  const totalCols = STICKY_COLS + 1 + (fbaOpen ? 5 : 3) + 1 + (shipmentOpen ? 3 : 0) + 1
    + (prodPlanOpen ? prodDays.length : 0) + 7 + (daysActOpen ? 3 : 1) + (daysAiOpen ? 3 : 1) + 1 + 3 + 1;

  const colFilterPopup = (col, label) => (
    <ColumnFilterPopup
      label={label}
      filter={colFilters[col]}
      onChange={f => setColFilters(prev => ({ ...prev, [col]: f }))}
      onClear={() => setColFilters(prev => { const n = { ...prev }; delete n[col]; return n; })}
    />
  );

  // Totals for Boxes, Units and SKU types (across all visible rows)
  const { totalBoxes, totalUnits, totalSkuTypes } = useMemo(() => {
    let boxes = 0, units = 0, skuTypes = 0;
    grouped.forEach(({ rows }) => {
      rows.forEach(product => {
        const sku = product.SKU;
        const bagsPerBox = Number(product.bagsPerBox) || 0;
        const boxInput = boxInputs[sku];
        if (boxInput !== undefined && boxInput !== '') {
          const num = Number(boxInput);
          if (num > 0) {
            boxes += num;
            units += num * bagsPerBox;
            skuTypes += 1;
          }
        }
      });
    });
    return { totalBoxes: boxes, totalUnits: units, totalSkuTypes: skuTypes };
  }, [grouped, boxInputs]);

  return (
    <div className="sp-table-container">
      <table className="sp-table">
        <thead>
          {/* Row 1: Group headers */}
          <tr className="sp-thead-group">
            <th className="sp-th sp-th-sticky sp-th-sticky-1 sp-th-group" rowSpan={2}>#</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-2 sp-th-group" rowSpan={2}>Product Name</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-3 sp-th-group" rowSpan={2}>Brand</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-4 sp-th-group" rowSpan={2}>ASIN</th>
            <th className="sp-th sp-th-sticky sp-th-sticky-5 sp-th-group" rowSpan={2}>SKU</th>
            <th className="sp-th sp-th-group sp-block-left" rowSpan={2}>OOS</th>
            <th className="sp-th sp-th-group-header" colSpan={fbaOpen ? 5 : 3}>Stock</th>
            <th className="sp-th sp-th-group sp-th-reserved-boxes" rowSpan={2} title="Total quantity of all boxes not yet added to shipments">
              Reserved<br/>boxes
              <button
                className={`sp-prod-toggle${shipmentOpen ? '' : ' collapsed'}`}
                onClick={toggleShipment}
                title={shipmentOpen ? 'Hide Shipment columns' : 'Show Shipment columns'}
              >
                <i className={`fas fa-${shipmentOpen ? 'minus' : 'plus'}`}></i>
              </button>
              {colFilterPopup('reserved', 'Reserved boxes')}
            </th>
            {shipmentOpen && <th className="sp-th sp-th-group-header" colSpan={3}>Shipment</th>}
            <th className="sp-th sp-th-group sp-th-boxes-canship" rowSpan={2} title="includes WH stock and boxes to be produced by the ship date; excludes reserved boxes">
              Est. available<br/>boxes
              <button
                className={`sp-prod-toggle${prodPlanOpen ? '' : ' collapsed'}`}
                onClick={toggleProdPlan}
                title={prodPlanOpen ? 'Hide Production Plan' : 'Show Production Plan'}
              >
                <i className={`fas fa-${prodPlanOpen ? 'minus' : 'plus'}`}></i>
              </button>
            </th>
            {prodPlanOpen && (
              <th className="sp-th sp-th-group-header sp-th-prod" colSpan={prodDays.length}>
                Production Plan
              </th>
            )}
            <th className="sp-th sp-th-group-header sp-th-sales sp-block-left" colSpan={7}>Sales</th>
            {DAYS_GROUPS.map(group => {
              const open = daysOpen[group.id];
              return (
                <th
                  key={group.id}
                  className={`sp-th sp-th-days sp-block-left ${open ? 'sp-th-group-header' : 'sp-th-group sp-th-days-collapsed'}`}
                  colSpan={open ? group.cols.length : 1}
                  rowSpan={open ? 1 : 2}
                >
                  Days Supplied{open ? ' · ' : <br/>}{group.label}
                  <button
                    className={`sp-prod-toggle${open ? '' : ' collapsed'}`}
                    onClick={() => toggleDaysGroup(group)}
                    title={`${open ? 'Hide' : 'Show'} Days Supplied by ${group.label}`}
                  >
                    <i className={`fas fa-${open ? 'minus' : 'plus'}`}></i>
                  </button>
                </th>
              );
            })}
            <th className="sp-th sp-th-sep" rowSpan={2}></th>
            <th className="sp-th sp-th-group-header" colSpan={3}>
              Shipment Details
              <span className="sp-shipdetail-actions">
                <button
                  className="sp-shipdetail-btn sp-shipdetail-save"
                  onClick={onSaveBoxInputs}
                  disabled={boxSaveStatus === 'saving'}
                  title="Save planned boxes to database"
                >
                  <i className={`fas fa-${boxSaveStatus === 'saving' ? 'circle-notch sp-spin' : 'save'}`}></i>
                </button>
                {boxSaveStatus === 'saved' && <i className="fas fa-check sp-shipdetail-ok" title="Saved!"></i>}
                {boxSaveStatus === 'error'  && <i className="fas fa-times sp-shipdetail-err" title="Save error"></i>}
                <button
                  className="sp-shipdetail-btn sp-shipdetail-clear"
                  onClick={onClearBoxInputs}
                  title="Clear all planned box quantities"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </span>
            </th>
            <th className="sp-th sp-th-group sp-th-comments" rowSpan={2} title="Free-text comments per SKU">Comments</th>
          </tr>

          {/* Row 2: Sub-headers */}
          <tr className="sp-thead-sub">
            <th className="sp-th sp-th-sub sp-block-left">
              FBA
              <button
                className={`sp-prod-toggle${fbaOpen ? '' : ' collapsed'}`}
                onClick={toggleFba}
                title={fbaOpen ? 'Hide Available / Inbound' : 'Show Available / Inbound'}
              >
                <i className={`fas fa-${fbaOpen ? 'minus' : 'plus'}`}></i>
              </button>
              {fbaUpdatedAt && <div className="sp-th-updated">{fmtTimestamp(fbaUpdatedAt)}</div>}
            </th>
            {fbaOpen && <>
              <th className="sp-th sp-th-sub" title="available + Reserved FC Transfer + Reserved FC Processing (without inbound)">Available</th>
              <th className="sp-th sp-th-sub" title="inbound-quantity (sum by ASIN)">Inbound</th>
            </>}
            <th className="sp-th sp-th-sub" title="Available in AWD + Inbound to AWD — click a cell to correct the total">
              AWD
              {colFilterPopup('awd', 'AWD')}
              {awdUpdatedAt && <div className="sp-th-updated">{fmtTimestamp(awdUpdatedAt)}</div>}
            </th>
            <th className="sp-th sp-th-sub">WH</th>
            {shipmentOpen && <>
              <th className="sp-th sp-th-sub" title="Boxes Needed (sum by ASIN from Marketplace Shipments)">Needed<br/>box</th>
              <th className="sp-th sp-th-sub" title="Boxes Added (sum by ASIN from Marketplace Shipments)">Added<br/>box</th>
              <th className="sp-th sp-th-sub" title="Boxes Added × Bags per Box">Added<br/>pcs</th>
            </>}
            {prodPlanOpen && prodDays.map(d => (
              <th key={d} className="sp-th sp-th-sub sp-th-day">{fmtDateFull(d)}</th>
            ))}
            <th className="sp-th sp-th-sub sp-block-left">
              Avg Daily Sales (14d)
              {onOpenRangePopup && (
                <button className="sp-vel-btn" onClick={() => onOpenRangePopup('v14')} title="Custom date range">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              )}
              {velUpdatedAt.ts14   && <div className="sp-th-updated">{fmtTimestamp(velUpdatedAt.ts14)}</div>}
              {velUpdatedAt.from14 && <div className="sp-th-updated">{fmtDateShort(velUpdatedAt.from14)} → {fmtDateShort(velUpdatedAt.to14)}</div>}
            </th>
            <th className="sp-th sp-th-sub">
              Avg Daily Sales (7d)
              {onOpenRangePopup && (
                <button className="sp-vel-btn" onClick={() => onOpenRangePopup('v7')} title="Custom date range">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              )}
              {velUpdatedAt.ts7   && <div className="sp-th-updated">{fmtTimestamp(velUpdatedAt.ts7)}</div>}
              {velUpdatedAt.from7 && <div className="sp-th-updated">{fmtDateShort(velUpdatedAt.from7)} → {fmtDateShort(velUpdatedAt.to7)}</div>}
            </th>
            <th className="sp-th sp-th-sub">Trend (7 vs 14)</th>
            <th className="sp-th sp-th-sub">
              Actual Sales
              {onOpenRangePopup && (
                <button className="sp-vel-btn" onClick={() => onOpenRangePopup('actual')} title="Custom date range">
                  <i className="fas fa-calendar-alt"></i>
                </button>
              )}
              {actualSalesMeta.updatedAt && <div className="sp-th-updated">{fmtTimestamp(actualSalesMeta.updatedAt)}</div>}
              {actualSalesMeta.dateFrom  && <div className="sp-th-updated">{fmtDateShort(actualSalesMeta.dateFrom)} → {fmtDateShort(actualSalesMeta.dateTo)}</div>}
            </th>
            <th className="sp-th sp-th-sub" title="Monthly sales based on the average daily sales over the last 7 days">Est. monthly sales</th>
            <th className="sp-th sp-th-sub" title="AI forecast for the current month. Grey = YoY forecast, shown where the AI has none">Forecast Sales (AI)</th>
            <th className="sp-th sp-th-sub" title="Actual / Forecast">Act/Fct %</th>
            {DAYS_GROUPS.filter(group => daysOpen[group.id]).map(group => group.cols.map(([col, label, hint], i) => (
              <th key={col} className={`sp-th sp-th-sub sp-th-sortable${i === 0 ? ' sp-block-left' : ''}`} title={`${hint} — click to sort`} onClick={() => handleDaysSort(col)}>
                {label}
                <i className={`fas fa-sort${daysSort.col === col ? (daysSort.dir === 'asc' ? '-up' : '-down') : ''} sp-sort-icon${daysSort.col === col ? ' active' : ''}`}></i>
                {colFilterPopup(col, `${label} (days, ${group.label})`)}
              </th>
            )))}
            <th className="sp-th sp-th-sub">Bags/Box</th>
            <th className="sp-th sp-th-sub">Boxes</th>
            <th className="sp-th sp-th-sub">Units</th>
          </tr>
        </thead>

        <tbody>
          {grouped.length === 0 ? (
            <tr>
              <td className="sp-empty-state" colSpan={totalCols}>
                <i className="fas fa-box-open"></i>
                <span>No products found</span>
              </td>
            </tr>
          ) : (
            grouped.map(({ type, rows }) => (
              <React.Fragment key={type}>
                <tr className="sp-type-separator">
                  <td colSpan={totalCols} className="sp-type-label">
                    <div className="sp-type-label-inner">
                      <button
                        className={`sp-collapse-btn${collapsedTypes[type] ? ' collapsed' : ''}`}
                        onClick={() => toggleCollapse(type)}
                        title={collapsedTypes[type] ? 'Expand' : 'Collapse'}
                      >
                        <i className={`fas fa-${collapsedTypes[type] ? 'plus' : 'minus'}`}></i>
                      </button>
                      {TYPE_LABELS[type] || type}
                      <span className="sp-type-count">{rows.length}</span>
                    </div>
                  </td>
                </tr>

                {!collapsedTypes[type] && rows.map((product, rowIdx) => {
                  const sku = product.SKU;
                  const bagsPerBox = Number(product.bagsPerBox) || 0;
                  const stockWH = Number(product.stock_wh) || 0;
                  const stockFBA = Number(product.stock_fba) || 0;
                  const stockAWD = Number(product.stock_awd) || 0;
                  const shipNeededBox = Number(product.shipment_needed_box) || 0;
                  const shipAddedBox = Number(product.shipment_added_box) || 0;
                  const shipAddedPcs = shipAddedBox * bagsPerBox;
                  const willBeTaken = bagsPerBox > 0 ? shipAddedPcs - shipNeededBox * bagsPerBox : 0;
                  const prodSum = prodDays.reduce((sum, d) => sum + (planLookup[sku]?.[d] || 0), 0);
                  const producedSum = prodDays.reduce((sum, d) => sum + (producedData[sku]?.[d] || 0), 0);
                  const remainingProd = Math.max(0, prodSum - producedSum);
                  const boxesCanShip = bagsPerBox > 0
                    ? Math.floor((stockWH + willBeTaken + remainingProd) / bagsPerBox)
                    : 0;

                  const boxInput = boxInputs[sku] !== undefined ? boxInputs[sku] : '';
                  const units = bagsPerBox > 0 && boxInput !== '' ? bagsPerBox * Number(boxInput) : '';
                  const isOverLimit = boxInput !== '' && Number(boxInput) > 0 && Number(boxInput) > boxesCanShip;
                  const warningMsg = bagsPerBox === 0
                    ? 'Bags per Box not configured'
                    : `Exceeds est. available boxes (${boxesCanShip})`;

                  const fbaAvailable = Number(product.stock_fba_available) || 0;
                  const fbaInbound   = Number(product.stock_fba_inbound) || 0;
                  const days = calcDays(product);

                  // Forecast Sales: the AI agent's current month, our YoY forecast where it has none
                  const aiMonthUnits = aiForecast.month?.[product.ASIN]?.[aiMonthKey]?.value;
                  const forecastUnits = aiMonthUnits != null ? aiMonthUnits : forecastSalesData[sku]?.total_units;

                  const willBeTakenNeg = willBeTaken < 0;

                  return (
                    <tr key={sku} className={`sp-row ${rowIdx % 2 === 0 ? 'sp-row-even' : 'sp-row-odd'}`}>
                      <td className="sp-td sp-td-sticky sp-td-sticky-1 sp-td-num">{rowIdx + 1}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-2 sp-td-name" title={product.name}>{product.name}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-3 sp-td-brand">{product.brandID || '—'}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-4 sp-td-asin">{product.ASIN || '—'}</td>
                      <td className="sp-td sp-td-sticky sp-td-sticky-5 sp-td-sku">{sku}</td>

                      {/* OOS */}
                      <td className="sp-td sp-td-oos sp-block-left">
                        {product.ASIN
                          ? stockFBA === 0
                            ? <i className="fas fa-check sp-oos-check" title="Out of Stock on Amazon"></i>
                            : '—'
                          : '—'}
                      </td>

                      {/* Stock */}
                      <td
                        className="sp-td sp-td-stock"
                        title={product.ASIN && stockFBA > 0
                          ? `Sellable now: ${Number(product.stock_fba_avail_now) || 0} (= Sellerise "Available")`
                            + `\nMoving between FCs (transfer/processing): ${Number(product.stock_fba_fc_moves) || 0}`
                            + `\nInbound: ${Number(product.stock_fba_inbound) || 0}`
                            + `\nSold units awaiting shipment are NOT counted here.`
                          : undefined}
                      >{stockFBA > 0 ? stockFBA : '—'}</td>
                      {fbaOpen && <>
                        {['available', 'inbound'].map(field => (
                          <td key={field} className="sp-td sp-td-stock">
                            {product.ASIN ? (
                              <InlineEdit
                                value={field === 'available' ? fbaAvailable : fbaInbound}
                                className="sp-inline-input sp-fba-input"
                                title={`Manual ${field} stock for ${product.ASIN}`}
                                onSave={v => {
                                  if (v === '' || isNaN(Number(v)) || Number(v) < 0) return false;
                                  return onSaveFbaStock && onSaveFbaStock(product.ASIN, field, v);
                                }}
                              />
                            ) : '—'}
                          </td>
                        ))}
                      </>}
                      <td className="sp-td sp-td-stock">
                        {product.ASIN ? (
                          <InlineEdit
                            value={stockAWD || ''}
                            className="sp-inline-input sp-fba-input"
                            title={`Available in AWD: ${Number(product.stock_awd_available) || 0}`
                              + `\nInbound to AWD: ${Number(product.stock_awd_inbound) || 0}`
                              + `\nType the real total to correct it — the next Amazon sync overwrites the correction.`}
                            onSave={v => {
                              if (v === '' || isNaN(Number(v)) || Number(v) < 0) return false;
                              return onSaveAwdStock && onSaveAwdStock(product.ASIN, v);
                            }}
                          />
                        ) : '—'}
                      </td>
                      <td className="sp-td sp-td-stock">{stockWH > 0 ? stockWH : '—'}</td>

                      {/* Reserved boxes */}
                      <td className={`sp-td sp-td-wh-ship${willBeTakenNeg ? ' sp-td-negative' : ''}`}>
                        {bagsPerBox > 0 ? willBeTaken : '—'}
                      </td>

                      {/* Shipment: Needed / Added / Added pcs */}
                      {shipmentOpen && <>
                        <td className="sp-td sp-td-ship-needed">{shipNeededBox > 0 ? shipNeededBox : '—'}</td>
                        <td className="sp-td sp-td-ship-added">{shipAddedBox > 0 ? shipAddedBox : '—'}</td>
                        <td className="sp-td sp-td-ship-pcs">{bagsPerBox > 0 && shipAddedBox > 0 ? shipAddedPcs : '—'}</td>
                      </>}

                      {/* Est. available boxes */}
                      <td className="sp-td sp-td-boxes-ship">
                        {bagsPerBox > 0 ? boxesCanShip : '—'}
                      </td>

                      {/* Production plan days */}
                      {prodPlanOpen && prodDays.map(d => {
                        const planned  = planLookup[sku]?.[d] || 0;
                        const produced = producedData[sku]?.[d] || 0;
                        const hasData  = planned > 0 || produced > 0;
                        return (
                          <td key={d} className={`sp-td sp-td-plan${planned > 0 ? ' sp-td-plan-has' : ''}`}>
                            {hasData ? (
                              <span className="sp-plan-produced">
                                <span className="sp-plan-planned">{planned || 0}</span>
                                <span className="sp-plan-divider">/</span>
                                <span className={`sp-plan-done${produced >= planned && planned > 0 ? ' sp-plan-done-full' : ''}`}>{produced}</span>
                              </span>
                            ) : '—'}
                          </td>
                        );
                      })}

                      {/* Sales block */}
                      <td className="sp-td sp-td-vel sp-block-left">
                        {velocityData[sku]?.vel_14d != null ? parseFloat(velocityData[sku].vel_14d).toFixed(1) : '—'}
                      </td>
                      <td className="sp-td sp-td-vel">
                        {velocityData[sku]?.vel_7d != null ? parseFloat(velocityData[sku].vel_7d).toFixed(1) : '—'}
                      </td>
                      {(() => {
                        const v7  = velocityData[sku]?.vel_7d;
                        const v14 = velocityData[sku]?.vel_14d;
                        if (v7 == null || v14 == null) return <td className="sp-td sp-td-trend">—</td>;
                        const n7 = parseFloat(v7), n14 = parseFloat(v14);
                        if (n7 > n14) return (
                          <td className="sp-td sp-td-trend sp-td-trend-up">
                            <i className="fas fa-caret-up sp-trend-up" title={`Vel 7d (${n7}) > Vel 14d (${n14})`}></i>
                          </td>
                        );
                        if (n7 < n14) return (
                          <td className="sp-td sp-td-trend sp-td-trend-down">
                            <i className="fas fa-caret-down sp-trend-down" title={`Vel 7d (${n7}) < Vel 14d (${n14})`}></i>
                          </td>
                        );
                        return <td className="sp-td sp-td-trend">—</td>;
                      })()}
                      <td className="sp-td sp-td-actual-sales">
                        {actualSalesData[sku]?.total_units != null ? actualSalesData[sku].total_units : '—'}
                      </td>
                      <td className="sp-td sp-td-run-rate">
                        {velocityData[sku]?.vel_7d != null
                          ? Math.round(parseFloat(velocityData[sku].vel_7d) * daysInCurrentMonth)
                          : '—'}
                      </td>
                      <td
                        className={`sp-td sp-td-forecast-sales${aiMonthUnits == null && forecastUnits != null ? ' sp-forecast-fallback' : ''}`}
                        title={forecastUnits == null ? undefined
                          : aiMonthUnits != null ? 'AI forecast' : 'YoY forecast — the AI has no forecast for this product'}
                      >
                        {forecastUnits != null ? forecastUnits : '—'}
                      </td>
                      {(() => {
                        const actual   = actualSalesData[sku]?.total_units;
                        const forecast = forecastUnits;
                        if (actual == null || forecast == null || Number(forecast) === 0) {
                          return <td className="sp-td sp-td-actfct">—</td>;
                        }
                        const pct = Math.round(Number(actual) / Number(forecast) * 100);
                        return <td className="sp-td sp-td-actfct">{pct}%</td>;
                      })()}

                      {/* Days Supplied blocks: by actual sales, by the AI forecast */}
                      {DAYS_GROUPS.map(group => !daysOpen[group.id]
                        ? <td key={group.id} className="sp-td sp-td-days sp-block-left"></td>
                        : group.cols.map(([col], i) => {
                          const extra = i === 0 ? ' sp-block-left' : '';
                          if (days[col] == null) return <td key={col} className={`sp-td sp-td-days${extra}`}>—</td>;
                          const n = Math.round(days[col]);
                          // past the forecast horizon the real cover is unknown — no colour
                          if (days.beyond[col]) return (
                            <td key={col} className={`sp-td sp-td-days${extra}`} title={`The AI forecast ends in ${n} days — the stock lasts longer`}>{n}+</td>
                          );
                          const cls = n < 30 ? ' sp-days-red' : n <= 60 ? ' sp-days-yellow' : '';
                          return <td key={col} className={`sp-td sp-td-days${extra}${cls}`}>{n}</td>;
                        }))}

                      {/* Separator */}
                      <td className="sp-td sp-td-sep-cell"></td>

                      {/* Shipment Details */}
                      <td className="sp-td sp-td-bags">{bagsPerBox > 0 ? bagsPerBox : '—'}</td>

                      {/* Boxes input with over-limit warning */}
                      <td className="sp-td sp-td-boxes-input">
                        <div className="sp-box-input-wrapper">
                          <input
                            type="number"
                            className={`sp-box-input${isOverLimit ? ' sp-box-input-warning' : ''}`}
                            min="0"
                            value={boxInput}
                            placeholder="0"
                            onChange={e => handleBoxInput(sku, e.target.value)}
                          />
                          {isOverLimit && (
                            <span className="sp-box-warning-icon" title={warningMsg}>
                              <i className="fas fa-exclamation-triangle"></i>
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="sp-td sp-td-units">{units !== '' ? units : '—'}</td>

                      {/* Comments */}
                      <td className="sp-td sp-td-comment">
                        <InlineEdit
                          type="text"
                          value={commentsData[sku] || ''}
                          className="sp-inline-input sp-comment-input"
                          placeholder="…"
                          title={commentsData[sku] || 'Add comment'}
                          onSave={v => { onSaveComment && onSaveComment(sku, v); }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>

        {/* Total row — only shown when at least one box input is filled */}
        {(totalBoxes > 0 || totalUnits > 0) && (
          <tfoot>
            <tr className="sp-tfoot-total">
              <td colSpan={totalCols - 4} className="sp-tfoot-label">
                Total shipment
                <span className="sp-tfoot-sku-types" title="Number of unique SKUs in shipment">
                  &nbsp;·&nbsp;{totalSkuTypes} SKU{totalSkuTypes !== 1 ? 's' : ''}
                </span>
              </td>
              <td className="sp-tfoot-empty"></td>
              <td className="sp-tfoot-total-val">{totalBoxes}</td>
              <td className="sp-tfoot-total-val">{totalUnits}</td>
              <td className="sp-tfoot-empty"></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default ShipmentsTable;
